import { takeLatest, select, put } from 'redux-saga/effects';
import * as cnt from './constants';
import { apiSaga, checkUpload } from '../../../../sagas';
import { masterTemplates as apis, contracting as contractingApis } from '../../../../api';
import { replace } from 'react-router-redux';
import { composeForPolicyNotes } from '../../__storeFiles__/composeDraftEditDataByType';
import editContractDetails from '../../../../stores/contracting/contract-activities-products/edit-contract-details';
import R from 'ramda';

function* getDocData() {
  const { docs } = yield select(({ contracting }) => contracting.masterTemplate);
  const {
    currentId,
  } = docs;
  return {
    currentId,
    docData: docs[currentId],
  };
}

function* getMasters() {
  const {
    type: policyType,
  } = yield select(state => state.contracting.masterTemplate.applyMasterToTemplatesReducer);
  yield* apiSaga(cnt.apply.GET_MASTERS, apis.getAll, [policyType]);
}

function* loadDoc() {
  const { currentRoute: pathname } = yield select(state => state.route);
  const [id] = pathname.split('/').slice(3, 4);
  if (id) yield* apiSaga(cnt.GET_DOC, apis.getOne, [id]);
}

function* getTemplateDrafts() {
  yield* apiSaga(cnt.apply.GET_TEMPLATES, contractingApis.getTemplateList, []);
}

function* init({ payload: { pathname, query } }) {
  // both doc and list page url has master-template in it
  if (pathname.indexOf('/master-template/') > -1) {
    yield loadDoc();
  }
  if (pathname.indexOf('/associate') > -1) {
    const [masterId] = pathname.split('/').slice(3, 4);
    yield getTemplateDrafts();
    yield put({
      type: cnt.apply.INIT,
      payload: {
        type: query.type,
        knownMasterId: masterId,
      },
    });
  }
  if (pathname.indexOf('pick-master-template') > -1) {
    const [templateDraftId] = pathname.split('/').slice(3, 4);
    yield put({
      type: cnt.apply.INIT,
      payload: {
        type: query.type,
        knownTemplateDraftId: templateDraftId,
      },
    });
    yield getMasters();
  }
}

function* loadDiff({ payload }) {
  if (payload.data.status === 'draft' && payload.data.originFrom === 'edited') {
    yield* apiSaga(cnt.GET_DIFF_SUMMARY, apis.getDiff, [{
      lhsDocId: payload.data.draftId,
      rhsDocId: `draft:${payload.data.draftId}`,
      onlyGetSummary: true,
    }]);
  }
}

function* doCreateEditedDraft({ type }) {
  const { currentId } = yield select(({ contracting }) => contracting.masterTemplate.docs);
  yield* apiSaga(type, apis.createEditedDraft, [currentId]);
}

function* routeToDraft({ args }) {
  yield put(replace(`/contracting/master-template/draft:${args[0]}`));
}

function* populateEditDialog() {
  const { docs, editDialog } = yield select(({ contracting }) => contracting.masterTemplate);
  const {
    currentId,
  } = docs;
  const composedData = composeForPolicyNotes(editDialog, docs[currentId]);
  yield put({ type: cnt.POPULATE_EDIT_DIALOG_DATA, payload: composedData });
}

function* getAssociatedTemplates() {
  const {
    docData,
    currentId,
  } = yield getDocData();
  const id = docData.status === 'draft' ? currentId.split(':')[1] : currentId;
  yield* apiSaga(cnt.GET_ASSOCIATED_TEMPLATES, apis.getAssociatedTemplates, [{ id, type: docData.type }]);
}

function* saveEdits({ type, payload }) {
  const { docs, editDialog } = yield select(({ contracting }) => contracting.masterTemplate);
  const {
    currentId,
  } = docs;
  const doc = docs[currentId];
  const composePayload = { data: editDialog.data, updateType: payload.updateType };
  if (payload.updateType === 'SAVE_BILLING_INFO_ADD_EDIT' && editDialog.data.insuranceFiles.length) {
    const nuFiles = [];
    for (const val of editDialog.data.insuranceFiles) {
      const uploaded = yield checkUpload(val, type);
      nuFiles.push(uploaded);
    }
    composePayload.data.insuranceFiles = nuFiles;
  }
  const processed = editContractDetails(doc, composePayload).contractDetails.toJS();
  const updates = R.keys(processed).reduce((result, key) => {
    return {
      ...result,
      [`${doc.type}.${key}`]: processed[key],
    };
  }, {});
  yield* apiSaga(type, apis.edit, [currentId.split(':')[1], updates]);
}

function* discardDraft({ type }) {
  const confirm = window.confirm('Are you sure you want to discard this draft?');
  if (confirm) {
    const { docs: { currentId } } = yield select(({ contracting }) => contracting.masterTemplate);
    yield* apiSaga(type, apis.discardDraft, [currentId.split(':')[1]]);
  }
}

function* routeToTemplate(id) {
  yield put(replace(`/contracting/master-template/${id}`));
}

function* finalise() {
  const {
    docData,
  } = yield getDocData();
  yield* apiSaga(cnt.FINALISE, apis.finalise, [docData.draftId]);
}

function* handleClickFinalise() {
  const {
    docData,
  } = yield getDocData();
  const {
    templates,
  } = docData;
  if (templates && templates.length) {
    yield put({ type: cnt.OPEN_FINALISE_DIALOG });
  } else {
    yield finalise();
  }
}

function* getAffectedBuyers() {
  const {
    docData,
  } = yield getDocData();
  const {
    draftId,
  } = docData;
  yield* apiSaga(cnt.GET_AFFECTED_BUYERS, apis.getAffectedBuyers, [draftId]);
}

/*
 payload = {
  newId,
  oldId: draftId,
  templateName,
  type,
  nStagedTemplates,
  buyers,
};
*/
function* handleFinaliseSuccess({ payload }) {
  yield routeToTemplate(payload.newId);
  if (payload.nStagedTemplates) {
    yield* apiSaga(
      cnt.SEND_UPDATES,
      contractingApis.supplierSendUpdates,
      [{}],
    );
  }
}

function* applyToTemplates({ type }) {
  const {
    applyMasterToTemplatesReducer: {
      allTemplatesDrafts,
      sameAsCorporatePolicyNotes,
      knownMasterId,
      knownTemplateDraftId,
      type: masterType,
      masterList,
      honorPreloadContent,
    },
  } = yield select(({ contracting }) => contracting.masterTemplate);
  yield* apiSaga(
    type,
    apis.applyMasterToTemplates,
    [{
      masterTemplateId: knownMasterId || masterList.find(obj => obj.checked)._id,
      type: masterType,
      templateDraftIds: knownTemplateDraftId
        ? [knownTemplateDraftId]
        : allTemplatesDrafts
          .filter(obj => obj.checked)
          .map(obj => obj.originalContractId || obj._id),
      sameAsCorporatePolicyNotes,
      honorPreloadContent,
    }],
  );
}

function* handleApplySuccess() {
  const {
    knownTemplateDraftId,
  } = yield select(({ contracting }) => contracting.masterTemplate.applyMasterToTemplatesReducer);
  if (knownTemplateDraftId) {
    yield put(replace(`/contracting/draft/${knownTemplateDraftId}/policies`));
    yield put({ type: cnt.apply.CLEAR_STORE });
  } else {
    yield getTemplateDrafts();
    yield getAssociatedTemplates();
  }
}
export default [
  takeLatest('@@router/LOCATION_CHANGE', init),
  takeLatest(`${cnt.GET_DOC}_SUCCESS`, loadDiff),
  takeLatest(`${cnt.GET_DOC}_SUCCESS`, getAssociatedTemplates),
  takeLatest(cnt.CREATE_EDITED_DRAFT, doCreateEditedDraft),
  takeLatest(`${cnt.CREATE_EDITED_DRAFT}_SUCCESS`, routeToDraft),
  takeLatest(cnt.INITIATE_EDIT_WITH_TYPE, populateEditDialog),
  takeLatest(cnt.SAVE_EDITS, saveEdits),
  takeLatest(`${cnt.SAVE_EDITS}_SUCCESS`, loadDoc),
  takeLatest(cnt.DISCARD_DRAFT, discardDraft),
  takeLatest(`${cnt.DISCARD_DRAFT}_SUCCESS`, function* fn({ args }) {
    yield routeToTemplate(args[0]);
  }),
  takeLatest(cnt.CLICK_FINALISE, handleClickFinalise),
  takeLatest(`${cnt.FINALISE}_SUCCESS`, handleFinaliseSuccess),
  takeLatest(cnt.OPEN_FINALISE_DIALOG, getAffectedBuyers),
  takeLatest(cnt.FINALISE, finalise),
  takeLatest(cnt.apply.GET_TEMPLATES, getTemplateDrafts),
  takeLatest(cnt.apply.GET_MASTERS, getMasters),
  takeLatest(cnt.apply.APPLY_TO_TEMPLATES, applyToTemplates),
  takeLatest(`${cnt.apply.APPLY_TO_TEMPLATES}_SUCCESS`, handleApplySuccess),
];



// WEBPACK FOOTER //
// ./src/app/components/contracting/master-template/__storeFiles__/sagas.js